import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  Alert,
  AlertIcon,
  Badge,
  Icon,
  Divider,
  Spinner,
  Center
} from '@chakra-ui/react';
import { FaFax, FaCog, FaEdit, FaQrcode, FaChartBar, FaBullhorn } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import FaxCredentials from './FaxCredentials';
import FaxEditor from './FaxEditor';
import QRCodeGenerator from './QRCodeGenerator';
import FaxAnalytics from './FaxAnalytics';
import FaxCampaign from './FaxCampaign';

export default function FaxManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [faxConfig, setFaxConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    fetchFaxConfig();
  }, []);

  const fetchFaxConfig = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('fax_config')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      setFaxConfig(data);
    } catch (error) {
      console.error('Error fetching fax config:', error);
      toast({
        title: 'Error loading fax configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfigUpdate = (updatedConfig) => {
    setFaxConfig(updatedConfig);
    toast({
      title: 'Fax configuration updated',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  if (loading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" />
          <Text>Loading fax management...</Text>
        </VStack>
      </Center>
    );
  }

  const isConfigured = faxConfig && faxConfig.access_id && faxConfig.access_pwd;

  return (
    <Box maxW="1200px" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <HStack spacing={4} mb={2}>
            <Icon as={FaFax} boxSize={8} color="blue.500" />
            <Text fontSize="2xl" fontWeight="bold">
              Fax Management
            </Text>
            {isConfigured ? (
              <Badge colorScheme="green" fontSize="sm">
                Configured
              </Badge>
            ) : (
              <Badge colorScheme="red" fontSize="sm">
                Not Configured
              </Badge>
            )}
          </HStack>
          <Text color="gray.600">
            Manage fax settings, create documents, and track QR code visits
          </Text>
        </Box>

        {/* Configuration Status Alert */}
        {!isConfigured && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">eFax not configured</Text>
              <Text fontSize="sm">
                Please configure your eFax credentials in the Settings tab before using fax features.
              </Text>
            </Box>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
          <TabList>
            <Tab>
              <HStack spacing={2}>
                <Icon as={FaCog} />
                <Text>Settings</Text>
              </HStack>
            </Tab>
            <Tab isDisabled={!isConfigured}>
              <HStack spacing={2}>
                <Icon as={FaEdit} />
                <Text>Fax Editor</Text>
              </HStack>
            </Tab>
            <Tab isDisabled={!isConfigured}>
              <HStack spacing={2}>
                <Icon as={FaQrcode} />
                <Text>QR Codes</Text>
              </HStack>
            </Tab>
            <Tab isDisabled={!isConfigured}>
              <HStack spacing={2}>
                <Icon as={FaChartBar} />
                <Text>Analytics</Text>
              </HStack>
            </Tab>
            <Tab isDisabled={!isConfigured}>
              <HStack spacing={2}>
                <Icon as={FaBullhorn} />
                <Text>Campaigns</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <FaxCredentials 
                config={faxConfig} 
                onUpdate={handleConfigUpdate}
              />
            </TabPanel>

            <TabPanel px={0}>
              {isConfigured ? (
                <FaxEditor config={faxConfig} />
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <Text>Please configure eFax credentials first.</Text>
                </Alert>
              )}
            </TabPanel>

            <TabPanel px={0}>
              {isConfigured ? (
                <QRCodeGenerator config={faxConfig} />
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <Text>Please configure eFax credentials first.</Text>
                </Alert>
              )}
            </TabPanel>

            <TabPanel px={0}>
              {isConfigured ? (
                <FaxAnalytics />
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <Text>Please configure eFax credentials first.</Text>
                </Alert>
              )}
            </TabPanel>

            <TabPanel px={0}>
              {isConfigured ? (
                <FaxCampaign config={faxConfig} />
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <Text>Please configure eFax credentials first.</Text>
                </Alert>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
}
